interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  active: boolean;
  organizations: {
    id: string;
    organizationName: string;
    b2bSubscriptions: [
      {
        id: string;
        package: string;
        status: string;
        licenseCount: number;
        nextBillingDate: number;
        toBeCancelled: boolean;
      }
    ];
  }[];
}

interface IStats {
  totalPracticeTime: {
    days: number;
    daysLabel: string;
    hours: number;
    hoursLabel: string;
    minutes: number;
    minutesLabel: string;
  };
  avgPracticeTime: {
    days: number;
    daysLabel: string;
    hours: number;
    hoursLabel: string;
    minutes: number;
    minutesLabel: string;
  };
  activeSubUsers: number;
}

interface UpdateOrganizationData {
  organizationName?: string;
  postalCode?: string;
  billingAddress?: string;
  country?: string;
  taxId?: string;
}

interface UpdateSubscriptionData {
  organizationId: string;
  licenseCount: number;
  prorationCheck: boolean;
}

interface ISingleOrganization {
  id: string;
  organizationName: string;
  postalCode: string;
  billingAddress: string;
  totalLicenses: number;
  country: string;
  taxId: string;
  b2bAdmins: [
    {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    }
  ];
  usedLicenses: number;
  active?: boolean;
}

interface IRecoveryRequest {
  email: string;
  source: string;
}

interface IRecoveryCredentials {
  token: string;
  password: string;
}

interface IInvitedStudent {
  email: string;
  status: string;
  id: string;
  orgId: string;
  expiresAt: string;
  firstName?: string;
  lastName?: string;
}

interface IGetInvitedStudentsParams {
  orgId: string;
  status?:
    | "Invitation Pending"
    | "Registration Pending"
    | "Active"
    | "Inactive";
}

interface ILoginResponse {
  b2bAdmin: User;
  token: IToken;
  status: number;
}

interface IToken {
  expiresIn: number;
  refreshExpiresIn: number;
  accessToken: string;
  refreshToken: string;
}

interface ICreateWithAdmin {
  organization: {
    organizationName: string;
    postalCode?: string;
    billingAddress?: string;
    totalLicenses?: number;
    country?: string;
    state?: string;
    city?: string;
    taxId?: string;
    b2bAdmin: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phoneNumber?: string;
    };
  };
}

interface ICreateWithAdminResponse {
  organization: {
    id: string;
    organizationName: string;
    postalCode: string;
    billingAddress: string;
    totalLicenses: number;
    country: string;
    taxId: string;
    b2bAdmins: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    }[];
    usedLicenses: number;
  };
}

interface ISendInvitationsRequest {
  emails: string[];
  orgId: string;
}

interface IResponse<T> {
  message: string;
  data: T;
}

interface IProcessGatewayRequest {
  packageName: PackageNames;
  organizationId: string;
  licenseCount: number;
  tier?: string;
}
interface IProcessGatewayResponse {
  sessionUrl: string;
}

interface IFinalizeGatewayRequest {
  token: string;
  success: boolean;
}

interface Country {
  id: number;
  name: string;
  iso3: string;
  iso2: string;
  numeric_code: string;
  phone_code: number;
  capital: string;
  currency: string;
  currency_name: string;
  currency_symbol: string;
  native: string | null;
  region: string;
  subregion: string;
  emoji: string;
  tld: string;
  latitude: string;
  longitude: string;
}

interface State {
  id: number;
  name: string;
  state_code: string;
}

enum PackageNames {
  "Biennial Subscription" = "Biennial Subscription",
  "Yearly Subscription" = "Yearly Subscription",
  "Quarterly Subscription" = "Quarterly Subscription",
}

interface IFilters {
  email?: string;
  firstName?: string;
  lastName?: string;
  search?: string;
  id?: number;
  verified?: boolean;
  hadPremium?: boolean;
  subUserId?: string;
  accountType?: string;
}

interface StudentStatsResponse {
  totalPracticeTime: PracticeTime;
  avgPracticeScore: number;
  avgPronunciationScore: number;
}

interface PracticeTime {
  days: number;
  daysLabel: string;
  hours: number;
  hoursLabel: string;
  minutes: number;
  minutesLabel: string;
}

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  practiceTime: number;
}

interface StudentsResponse {
  data: Student[];
  total: number;
  page: number;
  totalPages: number;
}

interface LevelResponse {
  currentLevel: number;
  totalTimeForNextLevel: number;
  remainingTimeForNextLevel: number;
}

interface FeedbackResponse {
  total: number;
  page: number;
  totalPages: number;
  feedbacks: Array<{
    id: string;
    date: string;
    totalPracticeTime: {
      days: number;
      daysLabel: string;
      hours: number;
      hoursLabel: string;
      minutes: number;
      minutesLabel: string;
    };
  }>;
}

interface ModeUsageResponse {
  modeBreakdown: {
    main?: number;
    callMode?: number;
    roleplay?: number;
    debate?: number;
    character?: number;
    sentenceMode?: number;
    photoMode?: number;
    dialogue?: number;
    word?: number;
  };
}

interface IPracticeData {
  date: string;
  totalPracticeTime: number;
}

export type IPracticeResponse = {
  total: number;
  page: number;
  totalPages: number;
  data: IPracticeData[];
};

interface SessionOverviewResponse {
  totalPracticeTime: {
    days: number;
    daysLabel: string;
    hours: number;
    hoursLabel: string;
    minutes: number;
    minutesLabel: string;
  };
  practiceScore: number;
  grammarTopics: string[];
  modeBreakdown: {
    main?: number;
    callMode?: number;
    roleplay?: number;
    debate?: number;
    character?: number;
    sentenceMode?: number;
    photoMode?: number;
    dialogue?: number;
  };
}

export { PackageNames };

export type {
  ISendInvitationsRequest,
  UpdateSubscriptionData,
  IRecoveryCredentials,
  UpdateOrganizationData,
  IRecoveryRequest,
  IInvitedStudent,
  IGetInvitedStudentsParams,
  ILoginResponse,
  ICreateWithAdmin,
  IResponse,
  ICreateWithAdminResponse,
  User,
  IStats,
  ISingleOrganization,
  IProcessGatewayRequest,
  IProcessGatewayResponse,
  IFinalizeGatewayRequest,
  State,
  Country,
  IFilters,
  StudentStatsResponse,
  PracticeTime,
  StudentsResponse,
  LevelResponse,
  FeedbackResponse,
  ModeUsageResponse,
  SessionOverviewResponse,
};
